import type { LocalizedText } from '@mentorivo/shared';

import type { StudioFormMode } from '@/lib/creative-studio';

/**
 * ÖRNEK VİDEO KATALOĞU — her video sekmesinin altındaki tek kart.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Örnekler UYDURULMADI — hepsi bu ürünle üretilmiş GERÇEK çıktılar
 * ═══════════════════════════════════════════════════════════════════════════
 * Üçü de kütüphanedeki `READY` kayıtlardan seçildi (16 Eyl 2026) ve dosyalar
 * `public/samples/video/` altına ALINDI — kiracının deposundan imzalı adresle
 * çekilmiyor. İki sebep: imzalı adres kısa ömürlü (kart her açılışta yenisini
 * istemek zorunda kalırdı) ve bu dosyalar artık bir kiracının verisi değil,
 * ürünün vitrini. Kiracı o kreatifi silse bile örnek yerinde durmalı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 ÜÇÜNCÜ TARAF MARKASI TAŞIYAN ÇIKTI ELENDİ
 * ═══════════════════════════════════════════════════════════════════════════
 * Şablon örneğinin kaynağı tanınmış bir spor ayakkabıydı ve videonun SON
 * çekiminde markanın işareti okunaklı biçimde beliriyordu. Kendi vitrinimizde
 * başka bir markanın logosunu göstermek, hem izinsiz bir kullanım hem de
 * "bu marka bizi kullanıyor" izlenimi olurdu (`hero-brands` şeridinde aynı
 * tuzak yazılı). Dosya o çekimden ÖNCE kesildi (8,4 sn) ve şablon örneğinde
 * girdi fotoğrafı hiç gösterilmiyor — o kare markayı taşıyordu.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Metinler burada, `messages/*.json`da DEĞİL
 * ═══════════════════════════════════════════════════════════════════════════
 * Örneğin prompt'u bir arayüz metni değil, örneğin KENDİSİ — dosyayla birlikte
 * değişiyor ve birlikte değişmesi gerekiyor. Çeviri dosyasına koysaydık bir
 * gün videoyu değiştiren kişi metni bulamazdı. Kartın çerçeve metinleri
 * ("Örnek videoyu görüntüle", başlık) i18n'de; oradakiler videodan bağımsız.
 *
 * ⚠️ İngilizce karşılıklar ÇEVİRİ, yeni bir örnek değil: aynı video, aynı
 * girdi. Kullanıcı dil değiştirdiğinde farklı bir örnek görmemeli.
 *
 * Diğer on bir dil (29 Eyl 2026) bu nesnelere yazılmıyor: çeviri betiği
 * `{ tr, en }` nesnelerini kaynaktan topluyor ve `dataText`e yazıyor; ekran
 * `useLocalizedText()` ile seçiyor. Nesnenin şeklini bozma — betik onu
 * tanıyamaz ve metin İngilizce kalır.
 */
export interface VideoSampleField {
  label: LocalizedText;
  value: string;
}

export interface VideoSample {
  /** Hangi sekmenin altında duruyor. */
  mode: Extract<StudioFormMode, 'prompt' | 'template' | 'product'>;
  videoSrc: string;
  posterSrc: string;
  /** Girdi fotoğrafı — şablon örneğinde YOK (gerekçe dosya başlığında). */
  inputSrc: string | null;
  /** Videonun süresi (sn) ve oranı — oynatıcı kutuyu önceden kursun diye. */
  durationSeconds: number;
  ratio: number;
  /** Kullanıcının yazdığı metin. Ürün sekmesinde yok; orada `fields` var. */
  prompt?: LocalizedText;
  /** Ürün sekmesinin künyesi — kullanıcının doldurduğu kutular. */
  fields?: VideoSampleField[];
}

const BASE = '/samples/video';

export const VIDEO_SAMPLES: VideoSample[] = [
  {
    mode: 'prompt',
    videoSrc: `${BASE}/prompt.mp4`,
    posterSrc: `${BASE}/prompt-poster.jpg`,
    inputSrc: `${BASE}/prompt-input.jpg`,
    durationSeconds: 15,
    ratio: 9 / 16,
    prompt: {
      tr: 'Mankenin taktığı gözlük ana kahraman. Sade, açık tonlu bir şehir sabahı: cam bir cephenin yanında yumuşak yan ışık, arka plan hafif bulanık ve sakin. Kadraj yüzün üst yarısına odaklı; çerçeve ve camlar ilk kareden itibaren net. Tek bir yavaş hareket: hafif yakınlaşma, ardından menteşe ve sap detayına geçiş.',
      en: 'The sunglasses on the model are the lead. A plain, light-toned city morning: soft side light beside a glass façade, the background gently blurred and calm. The frame holds the upper half of the face; the frame and lenses are sharp from the first shot. One slow move: a light push-in, then a cut to the hinge and temple detail.',
    },
  },
  {
    mode: 'template',
    videoSrc: `${BASE}/template.mp4`,
    posterSrc: `${BASE}/template-poster.jpg`,
    inputSrc: null,
    durationSeconds: 8,
    ratio: 9 / 16,
    prompt: {
      tr: 'Şablon: Desen dönüşümü. Ürünün yüzeyi film boyunca malzeme değiştiriyor — süetten deriye, dokudan file yüzeye — ve her geçişte kadraj aynı kalıyor. Ek prompt yalnız konuyu söylüyor: açık gri spor ayakkabı.',
      en: 'Template: Pattern morph. The product’s surface changes material through the film — suede to leather, weave to mesh — while the framing stays put. The extra prompt only names the subject: a light grey sneaker.',
    },
  },
  {
    mode: 'product',
    videoSrc: `${BASE}/product.mp4`,
    posterSrc: `${BASE}/product-poster.jpg`,
    inputSrc: `${BASE}/product-input.jpg`,
    durationSeconds: 8,
    ratio: 9 / 16,
    fields: [
      { label: { tr: 'Marka adı', en: 'Brand name' }, value: 'Kynure' },
      { label: { tr: 'Kategori', en: 'Category' }, value: 'Cosmetics' },
      { label: { tr: 'Alt kategori', en: 'Subcategory' }, value: 'Skincare, face serum' },
    ],
  },
];

/** Sekmenin örneği — yoksa `null` ve kart hiç çizilmiyor. */
export function sampleForMode(mode: StudioFormMode): VideoSample | null {
  return VIDEO_SAMPLES.find((sample) => sample.mode === mode) ?? null;
}
